import { structurallyDistinctBoundaries, } from "../protocol/authority.js";
import { canonicalSha256, } from "../protocol/canonicalize.js";
// The SCHEMA half — this module is reachable from the client entry, and
// `evidence.js` drags the node-bound admission path in behind it.
import { INDEPENDENT_CHANGE_OBSERVATION_METHOD, INDEPENDENT_REPOSITORY_SNAPSHOT_OBSERVATION_METHOD, } from "../protocol/evidence-schema.js";
export const UNIVERSAL_DETERMINISTIC_EVALUATOR_REF = "excellent:deterministic:universal";
export const UNIVERSAL_DETERMINISTIC_EVALUATOR_VERSION = "1";
const EVALUATOR_SPEC = {
    ref: UNIVERSAL_DETERMINISTIC_EVALUATOR_REF,
    version: UNIVERSAL_DETERMINISTIC_EVALUATOR_VERSION,
    predicates: [
        "artifact_present",
        "content_digest_equals",
        "exit_code",
        "json_equals",
        "resource_bounds",
        "text_contains",
    ],
    failureMode: "NOT_RUN",
};
export function universalDeterministicEvaluatorDigest(sha256) {
    return canonicalSha256(EVALUATOR_SPEC, sha256);
}
function artifactsForObligation(obligation, evidence) {
    const kinds = new Set(obligation.evidence.map((requirement) => requirement.kind));
    return evidence.artifacts.filter((row) => kinds.has(row.artifact.kind));
}
/**
 * THE SELECTOR MISSED — which is a different fact from the predicate being false.
 *
 * `artifactsForObligation` above is a SELECTOR: it narrows the admitted bundle
 * to the artifacts whose `kind` the obligation's evidence requirements name. It
 * decides WHICH rows the predicate reads, never WHETHER the predicate holds.
 *
 * Three branches below are existential — `admitted.some(row => …)` — and
 * `.some()` over an empty array is vacuously FALSE. So when the selector matched
 * nothing, those branches read no artifact, compared no value, and returned FAIL
 * anyway. Measured through the real CLI on 2026-09-13: evidence registered with
 * `sourceKind: "artifact"`, a claim carrying the `claim-template` default
 * `evidenceKind: "attestation"`, and a pinned digest EQUAL to the registered one
 * byte for byte produced `status: FAIL`, `decision {decided: true,
 * predicateStatus: "FAIL"}`, `reasonCode: content-digest-mismatch`, "No admitted
 * artifact matches the contract-pinned content digest." The digest matched. The
 * receipt was signed. Nothing had read it.
 *
 * That receipt is indistinguishable from a real mismatch — same status, same
 * decision, same reason code, same rationale — so an auditor holding only the
 * receipt is told a false thing about a true claim, with the product's signature
 * on it.
 *
 * UNKNOWN, not a better-named FAIL. A FAIL asserts the proposition is false, and
 * renaming the reason code leaves that assertion standing over a comparison that
 * never happened. This is the same law two other places in this repository
 * already state:
 *
 *  - `resource_bounds` below returns UNKNOWN when its candidate set empties,
 *    because "a refusal here made no comparison, so asserting a violation would
 *    be a verdict the evaluator did not reach";
 *  - `adapters/typed-claim-predicate.ts` DECLINES `evidence.present` rather than
 *    binding `artifact_present`, because "the only route to that FAIL is 'no
 *    artifact of the required kind was cited', and reporting absence as
 *    falsehood converts 'we do not know' into 'it is false'. A fabricated FAIL
 *    is the same defect as a fabricated PASS."
 *
 * The three existential branches were the outliers, not the precedent.
 *
 * `artifact_present` is the ONE branch that must NOT call this: there, "nothing
 * of the required kind was admitted" IS the proposition, and `artifact-absent`
 * states it honestly.
 *
 * Cites NO artifact. The rows that exist are the ones the selector excluded, and
 * the citation rule applied to `resource_bounds` — "an artifact that could not
 * establish the obligation must not appear in the determination as though it
 * had" — forbids listing them as though they had been read. Their KINDS are
 * named in the rationale instead, because that is the fact the operator needs
 * and it is not an assertion that they were evaluated.
 */
function requiredKindUnmatched(obligation, evidence, admitted) {
    if (admitted.length > 0)
        return null;
    const quoted = (values) => values.length === 0 ? "none" : values.map((value) => JSON.stringify(value)).join(", ");
    const required = [...new Set(obligation.evidence.map((row) => row.kind))].sort();
    const present = [...new Set(evidence.artifacts.map((row) => row.artifact.kind))].sort();
    return result({
        status: "UNKNOWN",
        artifactIds: [],
        code: "evidence-kind-unmatched",
        rationale: `No admitted artifact carries an evidence kind this obligation selects, so no value was ` +
            `read and no comparison was made: this predicate was NOT evaluated, and nothing here says ` +
            `the claim is false. The obligation requires evidence of kind ${quoted(required)}; the ` +
            `admitted evidence carries ${quoted(present)}. Either state the kind the evidence was ` +
            `registered under, or cite evidence registered under a required kind.`,
    });
}
function result(input) {
    return {
        status: input.status,
        evidenceArtifactIds: input.artifactIds,
        reasonCodes: [input.code],
        rationale: input.rationale,
    };
}
function readPath(value, path) {
    let cursor = value;
    for (const segment of path.split(".").filter(Boolean)) {
        if (cursor === null ||
            Array.isArray(cursor) ||
            typeof cursor !== "object")
            return undefined;
        cursor = cursor[segment];
    }
    return cursor;
}
function exitCode(value) {
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
        const candidate = value.exitCode;
        return typeof candidate === "number" && Number.isInteger(candidate)
            ? candidate
            : null;
    }
    if (typeof value !== "string")
        return null;
    const match = value.match(/(?:exit(?:\s+code)?\s*[:=]\s*)(-?\d+)/i);
    return match ? Number(match[1]) : null;
}
/**
 * Is this admitted artifact an OBSERVATION of the changes, or the producer's
 * DECLARATION of them?
 *
 * §5.2 of the protocol spec: "A claim MAY trigger observation. It MUST NOT
 * independently establish a verified result." A `change_manifest` is a claim
 * unless somebody other than the party under verification went and looked. This
 * predicate is the difference, and it is why `resource_bounds` below may say
 * "independently observed" in its rationale at all.
 *
 * Until 2026-09-12 the `:bounds` determination read `changedResources` out of
 * whatever manifest the submission carried and glob-matched it. It consulted no
 * tree, no commit and no baseline, so the SUBJECT of the determination was
 * whatever range the producer chose to declare. Measured on the real repository
 * with production functions: declaring the true base produced FAIL over 357
 * files; declaring `head~1` instead — same head, same everything else —
 * produced PASS over 8, with the rationale "Every independently observed
 * changed resource is inside the contract bounds." Nothing had observed
 * anything. Stamping the honest pre-work baseline into
 * `binding.inputSnapshotDigest` — so the recorded baseline CONTRADICTED the
 * declared range — still produced PASS.
 *
 * Two conditions, and both are load-bearing:
 *
 *  1. **The method is the observation.** `INDEPENDENT_CHANGE_OBSERVATION_METHOD`
 *     is stamped by one writer, `directory-command-change-evidence.ts`, which
 *     diffs the two immutable trees inside the supervisor's bare object store —
 *     a store the producing container has no path to. On its own the label is
 *     just a string the assembler chose, which is condition 2's job.
 *  2. **The collection happened somewhere the producer is not.** Not "at a
 *     different label" — `independenceAxes` already compares labels, and two
 *     labels minted in one process satisfy it (see
 *     `structurallyDistinctBoundaries`). Both the collector's boundary and the
 *     custody boundary must be distinguishable from the producer's as units of
 *     execution.
 *
 * Condition 2 subsumes the admitted row's own `independence.classification`,
 * which is consulted first so this predicate routes through the protocol's one
 * independence notion rather than re-deciding it, and then STRENGTHENED rather
 * than replaced. A row admission already called non-independent is refused
 * here too.
 *
 * WHAT THIS DOES NOT ESTABLISH. An evidence submission is unsigned JSON, so a
 * party able to run two OS processes can still assemble a manifest naming the
 * second process's derived boundary. That limit belongs to the transport and is
 * the same one `git-tree-observed-oracle.ts` records: process-distinct, not
 * party-distinct. What is closed is the class that was live — one process
 * declaring its own changes under a second label.
 */
export function independentlyObservedChangeManifest(row) {
    const provenance = row.artifact.provenance;
    return (row.independence?.classification === "independent" &&
        (provenance.method === INDEPENDENT_CHANGE_OBSERVATION_METHOD ||
            provenance.method === INDEPENDENT_REPOSITORY_SNAPSHOT_OBSERVATION_METHOD) &&
        structurallyDistinctBoundaries(provenance.collector.executionBoundaryId, provenance.producerBoundaryId) &&
        structurallyDistinctBoundaries(provenance.custodyBoundaryId, provenance.producerBoundaryId));
}
/** Exported so `adapters/obligation-falsifiability.ts` can decide whether a bounds
 * obligation still has a failing world using THIS matcher. A second copy of these glob
 * semantics would be a fork that drifts silently, and the falsifiability verdict would
 * then describe an evaluator that is not the one the engine runs. */
export function resourceMatches(pattern, resource) {
    let expression = "^";
    for (let index = 0; index < pattern.length; index += 1) {
        const character = pattern[index];
        if (character === "*" && pattern[index + 1] === "*") {
            expression += ".*";
            index += 1;
        }
        else if (character === "*") {
            expression += "[^/]*";
        }
        else {
            expression += character.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        }
    }
    return new RegExp(`${expression}$`, "u").test(resource);
}
export function createUniversalDeterministicEvaluator(input) {
    return {
        manifest: {
            ref: UNIVERSAL_DETERMINISTIC_EVALUATOR_REF,
            version: UNIVERSAL_DETERMINISTIC_EVALUATOR_VERSION,
            digest: universalDeterministicEvaluatorDigest(input.sha256),
            authority: "deterministic",
            actor: input.actor,
        },
        evaluate({ obligation, evidence }) {
            const admitted = artifactsForObligation(obligation, evidence);
            const artifactIds = admitted.map((row) => row.artifact.id);
            const parameters = obligation.predicate.parameters;
            switch (obligation.predicate.kind) {
                case "artifact_present":
                    return result({
                        status: admitted.length > 0 ? "PASS" : "FAIL",
                        artifactIds,
                        code: admitted.length > 0 ? "artifact-present" : "artifact-absent",
                        rationale: admitted.length > 0
                            ? "The contract-required artifact is present in the admitted evidence graph."
                            : "The contract-required artifact is absent from the admitted evidence graph.",
                    });
                case "content_digest_equals": {
                    const expected = parameters.digest;
                    if (typeof expected !== "string") {
                        return result({
                            status: "NOT_RUN",
                            artifactIds: [],
                            code: "predicate-parameter-invalid",
                            rationale: "content_digest_equals requires a string digest parameter.",
                        });
                    }
                    const unmatched = requiredKindUnmatched(obligation, evidence, admitted);
                    if (unmatched)
                        return unmatched;
                    const passed = admitted.some((row) => row.artifact.contentDigest === expected);
                    return result({
                        status: passed ? "PASS" : "FAIL",
                        artifactIds,
                        code: passed ? "content-digest-matched" : "content-digest-mismatch",
                        rationale: passed
                            ? "An admitted artifact matches the contract-pinned content digest."
                            : "No admitted artifact matches the contract-pinned content digest.",
                    });
                }
                case "text_contains": {
                    const needle = parameters.needle;
                    if (typeof needle !== "string" || needle.length === 0) {
                        return result({
                            status: "NOT_RUN",
                            artifactIds: [],
                            code: "predicate-parameter-invalid",
                            rationale: "text_contains requires a non-empty needle parameter.",
                        });
                    }
                    const unmatched = requiredKindUnmatched(obligation, evidence, admitted);
                    if (unmatched)
                        return unmatched;
                    const passed = admitted.some((row) => typeof row.artifact.inlineContent === "string" &&
                        row.artifact.inlineContent.includes(needle));
                    return result({
                        status: passed ? "PASS" : "FAIL",
                        artifactIds,
                        code: passed ? "text-found" : "text-not-found",
                        rationale: passed
                            ? "The contract-pinned text occurs in admitted evidence."
                            : "The contract-pinned text does not occur in admitted evidence.",
                    });
                }
                case "json_equals": {
                    const path = parameters.path;
                    const expected = parameters.expected;
                    if (typeof path !== "string" || expected === undefined) {
                        return result({
                            status: "NOT_RUN",
                            artifactIds: [],
                            code: "predicate-parameter-invalid",
                            rationale: "json_equals requires path and expected parameters.",
                        });
                    }
                    const unmatched = requiredKindUnmatched(obligation, evidence, admitted);
                    if (unmatched)
                        return unmatched;
                    const expectedDigest = canonicalSha256(expected, input.sha256);
                    const passed = admitted.some((row) => {
                        if (row.artifact.inlineContent === null)
                            return false;
                        const actual = readPath(row.artifact.inlineContent, path);
                        return actual !== undefined &&
                            canonicalSha256(actual, input.sha256) === expectedDigest;
                    });
                    return result({
                        status: passed ? "PASS" : "FAIL",
                        artifactIds,
                        code: passed ? "json-value-matched" : "json-value-mismatch",
                        rationale: passed
                            ? "The value at the contract-pinned JSON path matches."
                            : "The value at the contract-pinned JSON path does not match.",
                    });
                }
                case "resource_bounds": {
                    const allowed = parameters.allowed;
                    const forbidden = parameters.forbidden;
                    if (!Array.isArray(allowed) ||
                        !allowed.every((value) => typeof value === "string") ||
                        !Array.isArray(forbidden) ||
                        !forbidden.every((value) => typeof value === "string")) {
                        return result({
                            status: "NOT_RUN",
                            artifactIds: [],
                            code: "predicate-parameter-invalid",
                            rationale: "resource_bounds requires string arrays for allowed and forbidden resources.",
                        });
                    }
                    // The SUBJECT gate. A manifest the producer authored is a claim about
                    // the changes; only an observation of them can establish this
                    // obligation. Refusing is UNKNOWN, never FAIL and never PASS: §5.4
                    // says insufficient evidence must not become success, and a refusal
                    // here made no comparison, so asserting a violation would be a
                    // verdict the evaluator did not reach.
                    const observed = admitted.filter(independentlyObservedChangeManifest);
                    if (observed.length === 0) {
                        return result({
                            status: "UNKNOWN",
                            artifactIds,
                            code: "change-manifest-not-independently-observed",
                            rationale: "No admitted change manifest was independently observed: every candidate was " +
                                "either declared by the producer or collected at an execution boundary that is " +
                                "not distinguishable from the producer's. A declaration of what changed is a " +
                                "claim, not evidence, so it cannot establish this obligation.",
                        });
                    }
                    const observedIds = observed.map((row) => row.artifact.id);
                    const manifests = observed
                        .map((row) => row.artifact.inlineContent)
                        .filter((value) => value !== null)
                        .map((value) => readPath(value, "changedResources"))
                        .filter((value) => Array.isArray(value));
                    if (manifests.length === 0 || manifests.some((manifest) => !manifest.every((value) => typeof value === "string"))) {
                        return result({
                            status: "UNKNOWN",
                            artifactIds: observedIds,
                            code: "change-manifest-unreadable",
                            rationale: "No independently observed change manifest contains a readable changedResources array.",
                        });
                    }
                    const changedResources = manifests.flat().filter((value) => typeof value === "string");
                    const outside = changedResources.filter((resource) => !allowed.some((pattern) => resourceMatches(pattern, resource)) ||
                        forbidden.some((pattern) => resourceMatches(pattern, resource)));
                    // Only the OBSERVED rows are cited. An artifact that could not
                    // establish the obligation must not appear in the determination as
                    // though it had.
                    return result({
                        status: outside.length === 0 ? "PASS" : "FAIL",
                        artifactIds: observedIds,
                        code: outside.length === 0 ? "resource-bounds-satisfied" : "resource-bounds-violated",
                        rationale: outside.length === 0
                            ? "Every independently observed changed resource is inside the contract bounds."
                            : `Changed resources violate the contract bounds: ${outside.join(", ")}.`,
                    });
                }
                case "exit_code": {
                    const expected = parameters.expected;
                    if (typeof expected !== "number" || !Number.isInteger(expected)) {
                        return result({
                            status: "NOT_RUN",
                            artifactIds: [],
                            code: "predicate-parameter-invalid",
                            rationale: "exit_code requires an integer expected parameter.",
                        });
                    }
                    const observed = admitted
                        .map((row) => row.artifact.inlineContent)
                        .map(exitCode)
                        .filter((value) => value !== null);
                    if (observed.length === 0) {
                        return result({
                            status: "UNKNOWN",
                            artifactIds,
                            code: "exit-code-unreadable",
                            rationale: "No admitted artifact contains a readable exit code.",
                        });
                    }
                    const passed = observed.every((value) => value === expected);
                    return result({
                        status: passed ? "PASS" : "FAIL",
                        artifactIds,
                        code: passed ? "exit-code-matched" : "exit-code-mismatch",
                        rationale: passed
                            ? `Every admitted exit code equals ${expected}.`
                            : `At least one admitted exit code does not equal ${expected}.`,
                    });
                }
                default:
                    return result({
                        status: "NOT_RUN",
                        artifactIds: [],
                        code: "predicate-kind-unsupported",
                        rationale: `The deterministic evaluator does not implement ${obligation.predicate.kind}.`,
                    });
            }
        },
    };
}
